// src/Demos/TelescopingSumDemo.js

/**
 * TelescopingSumDemo
 * 
 * Props:
 *   maxN: number (default 5) => how many terms to show
 *
 * Displays a table of each k in [1..maxN], 
 * the term = 1/(k(k+1)), partial fraction = 1/k - 1/(k+1),
 * and the running partial sum after k terms.
 */
export default function TelescopingSumDemo({ maxN = 5 }) {
  // We'll just compute an array of {k, term, partFrac, partialSum} for k=1..maxN
  const data = [];
  let runningSum = 0;

  for (let k = 1; k <= maxN; k++) {
    const term = 1 / (k * (k + 1)); 
    runningSum += term;

    // partial fraction => 1/k - 1/(k+1)
    // We'll store it as a string for display: "1/2 - 1/3", etc.
    const partFracStr = `1/${k} - 1/${k+1}`;

    data.push({
      k,
      term,
      partFrac: partFracStr,
      partialSum: runningSum
    });
  }

  // The closed-form result after maxN terms => 1 - 1/(maxN+1)
  const closedForm = 1 - 1/(maxN+1);

  return (
    <div style={{ margin: "1em 0" }}>
      <h5>Telescoping Sum Demo</h5>
      <p style={{ fontSize: "0.9em" }}>
        We show each term: <code>1 / [k(k+1)]</code> and how it 
        partially fractionizes into <code>1/k − 1/(k+1)</code>.
      </p>
      <table
        style={{
          width: "100%",
          maxWidth: 420,
          margin: "0 auto",
          borderCollapse: "collapse",
          fontSize: "0.9em"
        }}
      >
        <thead>
          <tr style={{ background: "#f5f5f5" }}>
            <th style={{ border: "1px solid #ccc", padding: 6 }}>k</th>
            <th style={{ border: "1px solid #ccc", padding: 6 }}>Term 1/[k(k+1)]</th>
            <th style={{ border: "1px solid #ccc", padding: 6 }}>Partial Fraction</th>
            <th style={{ border: "1px solid #ccc", padding: 6 }}>Partial Sum</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{row.k}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>
                {row.term.toFixed(4)}
              </td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>
                {row.partFrac}
              </td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>
                {row.partialSum.toFixed(4)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ fontSize: "0.9em", marginTop: 8 }}>
        After <code>{maxN}</code> terms, partial sum is <strong>{data[data.length-1].partialSum.toFixed(4)}</strong>.
        The closed-form is <code>1 - 1/({maxN}+1)</code> ≈ <strong>{closedForm.toFixed(4)}</strong>.
      </p>
      <p style={{ fontSize: "0.8em", fontStyle: "italic" }}>
        Notice how each intermediate <code>1/k − 1/(k+1)</code> cancels most terms, 
        leaving only <code>1 - 1/(n+1)</code> at the end.
      </p>
    </div>
  );
}
